/**
 * SkuSelector.jsx · v3.2.0 · Feb-2026
 * ────────────────────────────────────────────────────────────────
 * Dropdown de selección de SKU real para una línea de receta.
 * - match_mode='exact' → muestra el único SKU (readonly)
 * - match_mode='category' → dropdown con SKUs de la categoría (con stock)
 * Usa GET /v1/mesa/recipe-materials/{material_id}/resolve-skus
 */
import { useState, useEffect } from "react";
import api from "../lib/api";
import { Loader2, Package, Layers } from "lucide-react";
import { toast } from "sonner";

export default function SkuSelector({
  material,
  warehouseLocationId,
  value,
  onChange,
}) {
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const isCategory = material?.match_mode === "category";

  useEffect(() => {
    if (!material?.id) return;
    setLoading(true);
    const params = warehouseLocationId ? { warehouse_location_id: warehouseLocationId } : {};
    api.get(`/v1/mesa/recipe-materials/${material.id}/resolve-skus`, { params })
      .then((r) => {
        const skusList = r.data.skus || [];
        setSkus(skusList);
        // v3.2.0 · Modo exact → auto-selecciona el SKU único
        if (material.match_mode === "exact" && skusList.length > 0 && !value) {
          const sku = skusList[0];
          onChange({
            material_id: material.id,
            product_id: sku.product_id || null,
            product_name: sku.product_name,
            product_code: sku.product_code,
            stock_total: sku.stock_total,
            qty_used: material.qty_min || 1,
          });
        }
      })
      .catch(() => toast.error("Error cargando SKUs"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material?.id, warehouseLocationId]);

  if (!material) return null;

  return (
    <div className="border rounded-lg p-3 bg-white space-y-2" data-testid={`sku-selector-${material.id}`}>
      <div className="flex items-center gap-2 text-[11px]">
        {isCategory ? (
          <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-bold flex items-center gap-1">
            <Layers size={10}/> CATEGORÍA
          </span>
        ) : (
          <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-bold flex items-center gap-1">
            <Package size={10}/> EXACTO
          </span>
        )}
        <span className="font-medium truncate flex-1">
          {isCategory ? (material.product_category_name || material.product_name) : material.product_name}
        </span>
        <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">
          min:{material.qty_min} · max:{material.qty_max} · gratis:{material.qty_free}
        </span>
      </div>

      {loading ? (
        <div className="p-2 flex items-center justify-center"><Loader2 size={12} className="animate-spin text-gray-400"/></div>
      ) : !isCategory ? (
        <div className="text-[10px] text-blue-700 italic px-2">SKU fijo · asignado automáticamente</div>
      ) : skus.length === 0 ? (
        <div className="text-[10px] text-red-500 italic px-2">Sin SKUs disponibles en tu bodega para esta categoría</div>
      ) : (
        <select
          value={value?.product_id || ""}
          onChange={(e) => {
            const sku = skus.find((s) => String(s.product_id) === e.target.value);
            onChange(sku ? {
              material_id: material.id,
              product_id: sku.product_id,
              product_name: sku.product_name,
              product_code: sku.product_code,
              stock_total: sku.stock_total,
              qty_used: material.qty_min || 1,
            } : null);
          }}
          className="w-full text-xs px-2 py-1.5 border rounded"
          data-testid={`sku-select-${material.id}`}
        >
          <option value="">— elige un SKU —</option>
          {skus.map((s) => (
            <option key={s.product_id} value={s.product_id}>
              {s.product_name}{s.product_code ? ` · ${s.product_code}` : ""}{s.stock_total ? ` · stock ${s.stock_total}` : ""}
            </option>
          ))}
        </select>
      )}
      {value && (
        <div className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${
          (value.stock_total !== undefined && value.stock_total < (value.qty_used || 1))
            ? "bg-red-50 text-red-800 border border-red-300"
            : "bg-emerald-50 text-emerald-800"
        }`}>
          {(value.stock_total !== undefined && value.stock_total < (value.qty_used || 1))
            ? <>⚠️ Sin stock suficiente en tu bodega:</>
            : <>✅ Seleccionado:</>}
          <span className="font-bold truncate">{value.product_name}</span>
          {value.stock_total !== undefined && (
            <span className="text-[9px] opacity-75 font-mono ml-1">stock {value.stock_total}</span>
          )}
          <input
            type="number" min={1}
            value={value.qty_used || 1}
            onChange={(e) => onChange({ ...value, qty_used: Number(e.target.value) })}
            className="ml-auto w-14 text-[10px] px-1 py-0.5 border rounded"
            data-testid={`sku-qty-${material.id}`}
          />
        </div>
      )}
    </div>
  );
}
